// The instant a signature skill (or an artefact, a relic) goes off: each its own shape and motion, where most of them
// used to end in the same expanding ground ring. Ink like everything else — strokes, blots, hatched bits, no glow —
// and each over within about a second; the lasting parts (zones, status marks) stay in fx.ts. fx.ts resolves the
// event's hero and hands the handlers over as its own; this table only builds and moves.
import * as THREE from 'three';
import { mk, flat } from './engrave.js';
import { SKILLS } from '../core/skills.js';
import type { BoltKit } from './fxBolts.js';
import type { ViewHero } from './gameView.js';
import { FACE, TOWARD, strokeGeometry, blotGeometry } from './fxImpacts.js';

export type SkillKit = Pick<BoltKit, 'spawn' | 'puff' | 'fxMesh' | 'ink'> & {
  /** The rig's weapon hand, or chest height over the hero before a model with one has mounted. */
  handOf: (h: ViewHero, out: THREE.Vector3) => THREE.Vector3;
};
type Spot = { x: number; z: number };

const TAU = Math.PI * 2;
const GOLD = 0xf2d46b, STEEL = 0xe0d8bc, DUST = 0xcbbf9f, STONE = 0x8a8276, EMBER = 0xb8482a, SCORCH = 0x2a1210, SCORCH_RED = 0x5a1a14,
  BLOOD = 0x7a1a14, WIND = 0xa89c84, WARD_GLASS = 0xe4ead0, GLASS = 0xc8dcd4, FUR = 0x8a8478, AMBER = 0x8a5a10, LENS = 0xd8c890, MOTE = 0xf6e8a0;
// Астрал: Тень's dusk, Грех's crimson, Кошмар's bruise-violet and his crows' near-black (fx.ts's).
const DUSK = 0x3b3448, CRIMSON = 0xa8304a, BRUISE = 0x6a5a9a, CROW = 0x2b2430;

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
/** 0 → 1 over [0, a], 1 until b, back to 0 at 1: a flash's envelope over its life `k`. */
const env = (k: number, a: number, b: number) => (k < a ? k / a : k < b ? 1 : Math.max(0, 1 - (k - b) / (1 - b)));
/** `k` re-timed to start after `delay` (both fractions of a life): ≤ 0 until then. */
const after = (k: number, delay: number) => (k - delay) / (1 - delay);

export function heartGeometry() {
  const s = new THREE.Shape();
  s.moveTo(0, -0.17);
  s.bezierCurveTo(-0.3, 0.02, -0.15, 0.24, 0, 0.1);
  s.bezierCurveTo(0.15, 0.24, 0.3, 0.02, 0, -0.17);
  return new THREE.ExtrudeGeometry(s, { depth: 0.05, bevelEnabled: false });
}
const rect = (x: number, y: number, w: number, h: number) => new THREE.Shape([
  new THREE.Vector2(x, y), new THREE.Vector2(x + w, y), new THREE.Vector2(x + w, y + h), new THREE.Vector2(x, y + h)]);
const poly = (pts: number[]) => new THREE.Shape(Array.from({ length: pts.length / 2 }, (_, i) => new THREE.Vector2(pts[2 * i]!, pts[2 * i + 1]!)));
/* Сделка's marks: a cross, a chevron, a diamond. */
const RUNES = [
  () => new THREE.ShapeGeometry([rect(-0.04, -0.2, 0.08, 0.4), rect(-0.15, 0.04, 0.3, 0.07)]),
  () => new THREE.ShapeGeometry(poly([-0.18, 0.15, 0, -0.15, 0.18, 0.15, 0.1, 0.15, 0, -0.02, -0.1, 0.15])),
  () => new THREE.ShapeGeometry(poly([0, 0.2, 0.11, 0, 0, -0.2, -0.11, 0])),
];
/* Вызов's pennant, swallow-tailed, hung from x = 0. */
const pennantGeometry = () => new THREE.ShapeGeometry(poly([0, 0.3, 1.0, 0.18, 0.7, 0, 1.0, -0.18, 0, -0.3]));
/* Око бестиария: an almond between two arcs. */
function lensGeometry() {
  const pts: THREE.Vector2[] = [], n = 12;
  for (let i = 0; i <= n; i++) { const u = i / n; pts.push(new THREE.Vector2(-0.5 + u, Math.sin(Math.PI * u) * 0.24)); }
  for (let i = n - 1; i > 0; i--) { const u = i / n; pts.push(new THREE.Vector2(-0.5 + u, -Math.sin(Math.PI * u) * 0.24)); }
  return new THREE.ShapeGeometry(new THREE.Shape(pts));
}
export const featherGeometry = () => new THREE.SphereGeometry(0.3, 6, 4).scale(0.4, 0.12, 1.1);
const petalGeometry = () => new THREE.SphereGeometry(0.12, 6, 4).scale(0.9, 0.2, 0.6);

/** The shared motions of an ink instant (fxMoments.ts draws with them too). */
export function inkTools({ spawn, fxMesh }: Pick<SkillKit, 'spawn' | 'fxMesh'>) {
  // Knocked-loose bits are hatched and outlined like a unit (a small stippled piece dissolves into the paper at game
  // zoom); strokes and blots stay fx ink.
  const bit = (geo: THREE.BufferGeometry, color: number, double = false) => mk(geo, color, { team: 'unit', cast: false, double });
  const blots = [5, 13, 29].map(blotGeometry);
  const anyBlot = () => blots[Math.floor(Math.random() * blots.length)]!;

  /* Thrown from `from` with `vel`, falling under `g` until it lies on the ground; shrinks out over its last third. */
  function toss(key: string, build: () => THREE.Object3D, from: THREE.Vector3, vel: THREE.Vector3, dur: number, g: number, size = 1, spin = 8) {
    spawn(key, build, dur, (it) => {
      (it.p ||= new THREE.Vector3()).copy(from); (it.v ||= new THREE.Vector3()).copy(vel);
      it.g = g; it.s = size; it.spin = spin; it.ph = Math.random() * 6.3; it.d = dur;
      const drop = it.p.y - 0.22;
      it.land = g > 0 ? (it.v.y + Math.sqrt(it.v.y * it.v.y + 2 * g * Math.max(0, drop))) / g : Infinity;
    }, (it, k) => {
      const s = Math.min(k * it.d, it.land), o = it.obj;
      o.position.copy(it.p).addScaledVector(it.v, s);
      o.position.y = Math.max(0.22, o.position.y - 0.5 * it.g * s * s);
      o.rotation.set(it.ph + s * it.spin, it.ph * 2 + s * it.spin * 0.7, 0);
      o.scale.setScalar(it.s * Math.min(1, (1 - k) * 3) + 0.001);
    });
  }
  /* Something light let go at `at`: thrown by `vel`, slowed by the air, sinking with a sway — feathers, petals. */
  function sink(key: string, build: () => THREE.Object3D, at: THREE.Vector3, vel: THREE.Vector3, dur: number, fall: number, size = 1) {
    spawn(key, build, dur, (it) => {
      (it.p ||= new THREE.Vector3()).copy(at); (it.v ||= new THREE.Vector3()).copy(vel);
      it.ph = Math.random() * 6.3; it.fall = fall; it.s = size; it.d = dur;
    }, (it, k) => {
      const s = k * it.d, go = (1 - Math.exp(-3 * s)) / 3, o = it.obj;
      o.position.copy(it.p).addScaledVector(it.v, go);
      o.position.x += Math.sin(it.ph + k * 11) * 0.12 * k;
      o.position.y = Math.max(0.22, o.position.y - it.fall * s);
      o.rotation.set(Math.sin(it.ph + k * 9) * 0.7, it.ph + k * 2, Math.cos(it.ph + k * 8) * 0.5);
      o.scale.setScalar(it.s * (1 - k * k * k) + 0.001);
    });
  }
  /* An ink stroke on the ground from (x, z) out along `ang`, drawn out after `delay` (a fraction of its life), then thinning. */
  function groundStroke(color: number, len: number, w: number, x: number, z: number, ang: number, dur: number, delay = 0) {
    spawn('s:ground' + color + w, () => flat(fxMesh(strokeGeometry(1, w, 0), color, { double: true }), 0.25), dur, (it) => {
      it.obj.position.set(x, 0.25, z);
      it.obj.rotation.set(-Math.PI / 2, 0, -ang);
      it.len = len; it.dl = delay;
    }, (it, k) => {
      const u = after(k, it.dl);
      if (u <= 0) { it.obj.scale.set(0.001, 0.001, 1); return; }
      it.obj.scale.set(it.len * Math.min(1, u * 6) + 0.001, (u < 0.55 ? 1 : 1 - (u - 0.55) / 0.45) + 0.001, 1);
    });
  }
  /* A crack running out from `at`: straight strokes kinked where each ends, each drawn after the one before. */
  function crack(at: Spot, ang: number, r0: number, parts: number[], dur: number, color: number, w: number) {
    let x = at.x + Math.cos(ang) * r0, z = at.z + Math.sin(ang) * r0, a = ang;
    parts.forEach((len, i) => {
      groundStroke(color, len, w, x, z, a, dur, i * 0.08);
      x += Math.cos(a) * len; z += Math.sin(a) * len;
      a += rnd(-0.6, 0.6);
    });
  }
  /* A ground blot growing fast, held, then shrinking away. */
  function blot(color: number, x: number, z: number, size: number, dur: number, y = 0.23) {
    const geo = anyBlot();
    spawn('s:blot' + color + geo.uuid, () => flat(fxMesh(geo, color), y), dur, (it) => {
      it.obj.position.set(x, y, z); it.obj.rotation.z = Math.random() * TAU; it.s = size;
    }, (it, k) => it.obj.scale.setScalar(it.s * Math.min(1, k * 8) * (k > 0.6 ? 1 - (k - 0.6) / 0.4 : 1) + 0.001));
  }
  return { bit, anyBlot, toss, sink, groundStroke, crack, blot };
}

export function createSkillFx({ spawn, puff, fxMesh, ink, handOf }: SkillKit): Record<string, (e: any) => void> {
  const V = new THREE.Vector3(), P = new THREE.Vector3();
  const { bit, anyBlot, toss, sink, crack, blot } = inkTools({ spawn, fxMesh });
  /* Short pale streaks swirling round the legs: a haste taking. */
  function wind(hero: ViewHero, n: number) {
    for (let i = 0; i < n; i++) {
      spawn('s:wind', () => fxMesh(strokeGeometry(1.1, 0.11, 0.16), WIND, { double: true }), 0.6, (it) => {
        it.h = hero; it.a = i / n * TAU; it.y = 0.25 + (i % 3) * 0.2; it.r = 0.75 + (i % 2) * 0.2;
      }, (it, k) => {
        const h = it.h, a = it.a + k * 5;
        it.obj.position.set(h.pos.x + Math.cos(a) * it.r, it.y + k * 0.35 + h.lift, h.pos.z + Math.sin(a) * it.r);
        it.obj.rotation.set(-Math.PI / 2, 0, -(a + Math.PI / 2));  // along the orbit
        const g = env(k, 0.2, 0.4);
        it.obj.scale.set(g + 0.001, g + 0.001, 1);
      });
    }
  }
  /* Red embers lifting off the weapon hand, one after another: a damage buff taking. */
  function embers(hero: ViewHero, n: number) {
    for (let i = 0; i < n; i++) {
      spawn('s:handEmber', () => bit(new THREE.OctahedronGeometry(0.08), EMBER), 0.9, (it) => {
        it.h = hero; it.dl = i / n * 0.5; it.got = false;
        (it.v ||= new THREE.Vector3()).set(rnd(-0.4, 0.4), rnd(1.2, 1.8), rnd(-0.4, 0.4));
      }, (it, k) => {
        const u = after(k, it.dl), o = it.obj;
        if (u <= 0) { o.scale.setScalar(0.001); return; }
        if (!it.got) { handOf(it.h, it.p ||= new THREE.Vector3()); it.got = true; }  // where the hand is when it leaves
        o.position.copy(it.p).addScaledVector(it.v, u * 0.6);
        o.rotation.set(u * 9, u * 6, 0);
        o.scale.setScalar(1.3 * (1 - u) + 0.001);
      });
    }
  }
  /* A four-point glint on the hero, popping and turning. */
  function star(hero: ViewHero, dx: number, y: number, delay: number) {
    for (const a of [0, Math.PI / 2]) {
      spawn('s:star', () => bit(strokeGeometry(0.8, 0.13, 0).translate(-0.4, 0, 0), STEEL, true), 0.5, (it) => {
        it.h = hero; it.dx = dx; it.y = y; it.dl = delay; it.a = a;
      }, (it, k) => {
        const h = it.h, u = after(k, it.dl), g = u <= 0 ? 0 : u < 0.3 ? u / 0.3 : 1 - (u - 0.3) / 0.7;
        it.obj.position.set(h.pos.x + it.dx, it.y + h.lift, h.pos.z + TOWARD);
        it.obj.rotation.set(FACE, 0, it.a + Math.max(0, u) * 0.8);
        it.obj.scale.setScalar(g * 1.3 + 0.001);
      });
    }
  }

  return {
    /* Таран connecting: the ground cracks out from the victim, dust bursts; driven into a wall, longer cracks and
       stone chips knocked off it. */
    ramHit({ pos, big }: { pos: Spot; big?: boolean }) {
      const s = big ? 1.5 : 1, n = big ? 7 : 5, a0 = Math.random() * TAU;
      for (let i = 0; i < n; i++) crack(pos, a0 + i / n * TAU + rnd(-0.3, 0.3), 0.5, [rnd(0.7, 1) * s, rnd(0.5, 0.8) * s, rnd(0.3, 0.6) * s], 0.9, ink, 0.13);
      for (let i = 0; i < 6; i++) {
        const a = i / 6 * TAU + rnd(-0.3, 0.3);
        puff(P.set(pos.x + Math.cos(a) * 0.6, 0.3, pos.z + Math.sin(a) * 0.6), DUST, { size: 1.3 * s, rise: 0.35, dur: 0.7, drift: V.set(Math.cos(a) * 2.4, 0, Math.sin(a) * 2.4) });
      }
      if (!big) return;
      for (let i = 0; i < 8; i++) {
        const a = Math.random() * TAU, sp = rnd(2.5, 4.5);
        toss('s:stone', () => bit(new THREE.DodecahedronGeometry(0.16, 0), STONE), P.set(pos.x, 1.0, pos.z + 0.4),
          V.set(Math.cos(a) * sp, rnd(3, 5), Math.sin(a) * sp), 0.7, 14, rnd(0.8, 1.4));
      }
    },

    /* Упор armed: light running up the body and steel glints at the chest. */
    armed({ hero }: { hero: ViewHero }) {
      spawn('s:glintRun', () => bit(strokeGeometry(1.3, 0.18, 0).translate(-0.65, 0, 0), STEEL, true), 0.4, (it) => { it.h = hero; }, (it, k) => {
        const h = it.h;
        it.obj.position.set(h.pos.x + 0.35, 0.4 + k * 1.9 + h.lift, h.pos.z + TOWARD * 0.6);
        it.obj.rotation.set(FACE, 0, Math.PI / 2);
        it.obj.scale.set(Math.sin(Math.PI * k) + 0.001, 1, 1);
      });
      for (let i = 0; i < 3; i++) star(hero, rnd(-0.5, 0.5), rnd(1.7, 2.2), 0.12 + i * 0.1);
    },

    /* Вызов: gold spikes radiate from the knight, his banner snaps up over him. */
    rally({ hero }: { hero: ViewHero }) {
      for (let i = 0; i < 10; i++) {
        const a = i / 10 * TAU;
        spawn('s:spike', () => flat(fxMesh(new THREE.ConeGeometry(0.22, 1.6, 3), GOLD), 0.35), 0.45, (it) => {
          it.p = hero.pos.clone(); it.a = a;
        }, (it, k) => {
          const d = 1.2 + k * 3.5;
          it.obj.position.set(it.p.x + Math.cos(it.a) * d, 0.35, it.p.z + Math.sin(it.a) * d);
          it.obj.rotation.set(-Math.PI / 2, 0, -it.a - Math.PI / 2);
          it.obj.scale.setScalar(1 - k * 0.7);
        });
      }
      spawn('s:pole', () => bit(new THREE.BoxGeometry(0.07, 1.5, 0.07).translate(0, 0.75, 0), ink), 0.8, (it) => { it.h = hero; }, (it, k) => {
        const h = it.h, up = Math.min(1, k / 0.15), fade = k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1;
        it.obj.position.set(h.pos.x, 2.3 + h.lift, h.pos.z);
        it.obj.scale.set(fade + 0.001, up * fade + 0.001, fade + 0.001);
      });
      spawn('s:pennant', () => fxMesh(pennantGeometry(), GOLD, { double: true }), 0.8, (it) => { it.h = hero; }, (it, k) => {
        const h = it.h, out = Math.max(0, Math.min(1, (k - 0.12) / 0.18)), fade = k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1;
        it.obj.position.set(h.pos.x + 0.04, 3.5 + h.lift, h.pos.z);
        it.obj.rotation.set(FACE * 0.5, 0, Math.sin(k * 24) * 0.08);  // flapping
        it.obj.scale.set(out * fade + 0.001, fade + 0.001, 1);
      });
    },

    /* Боевой клич: shout strokes thrown out round the head, dust driven off the ground; each ally hasted gets wind
       of his own (`buff`). */
    warcry({ hero }: { hero: ViewHero }) {
      for (let i = 0; i < 8; i++) {
        const a = i / 8 * TAU + rnd(-0.15, 0.15);
        spawn('s:shout', () => fxMesh(strokeGeometry(0.7, 0.13, 0), 0xb06a30, { double: true }), 0.5, (it) => {
          it.h = hero; it.a = a; it.dl = (i % 2) * 0.15;
        }, (it, k) => {
          const u = after(k, it.dl), o = it.obj;
          if (u <= 0) { o.scale.set(0.001, 0.001, 1); return; }
          const h = it.h;
          o.position.set(h.pos.x, 2.0 + h.lift, h.pos.z + TOWARD * 0.5);
          o.rotation.set(FACE, 0, it.a);
          o.translateX(0.6 + u * 1.2);  // out along its own direction in the picture
          o.scale.set(Math.min(1, u * 4) * (1 - u * 0.6) + 0.001, 1 - u + 0.001, 1);
        });
      }
      for (let i = 0; i < 8; i++) {
        const a = i / 8 * TAU;
        puff(P.set(hero.pos.x + Math.cos(a) * 1.5, 0.2, hero.pos.z + Math.sin(a) * 1.5), DUST,
          { size: 1.1, rise: 0.4, dur: 0.7, drift: V.set(Math.cos(a) * 2, 0, Math.sin(a) * 2) });
      }
    },

    buff({ hero, buffType }: { hero: ViewHero; buffType: 'haste' | 'dmg' }) {
      if (buffType === 'dmg') embers(hero, 6); else wind(hero, 5);
    },
    windoil({ hero }: { hero: ViewHero }) { wind(hero, 7); },  // Ветряное масло: its haste, a stronger gust

    /* Сделка: marks on the ground close in on the devil while blood-ink drops spiral into him; the price paid, embers
       lift off his weapon hand. */
    pact({ hero }: { hero: ViewHero }) {
      for (let i = 0; i < 10; i++) {
        const kind = i % RUNES.length;
        spawn('s:rune' + kind, () => flat(fxMesh(RUNES[kind]!(), BLOOD, { double: true }), 0.26), 0.55, (it) => {
          it.h = hero; it.a = i / 10 * TAU;
        }, (it, k) => {
          const h = it.h, a = it.a - k * 1.5, d = 2.6 * (1 - k * k) + 0.2;
          it.obj.position.set(h.pos.x + Math.cos(a) * d, 0.26, h.pos.z + Math.sin(a) * d);
          it.obj.rotation.set(-Math.PI / 2, 0, -a + Math.PI / 2);  // each mark upright toward him
          it.obj.scale.setScalar(1.6 * Math.min(1, k / 0.15) * (1 - k * k * 0.8) + 0.001);
        });
      }
      for (let i = 0; i < 9; i++) {
        spawn('s:drop', () => fxMesh(new THREE.SphereGeometry(0.1, 8, 6), BLOOD), 0.5, (it) => {
          it.h = hero; it.a = i / 9 * TAU;
        }, (it, k) => {
          const a = it.a + k * 5, d = 2.2 * (1 - k);
          it.obj.position.set(it.h.pos.x + Math.cos(a) * d, 0.4 + k * 1.2, it.h.pos.z + Math.sin(a) * d);
        });
      }
      embers(hero, 7);
    },

    /* Прыжок coming down: a scorched, cracked blot, embers bursting up out of it, a little smoke. */
    leapLand({ pos }: { pos: Spot }) {
      blot(SCORCH, pos.x, pos.z, 1.6, 1.6);
      blot(SCORCH_RED, pos.x + rnd(-0.3, 0.3), pos.z + rnd(-0.3, 0.3), 0.9, 1.4, 0.235);
      const a0 = Math.random() * TAU;
      for (let i = 0; i < 6; i++) crack(pos, a0 + i / 6 * TAU + rnd(-0.3, 0.3), 1.0, [rnd(0.6, 0.9), rnd(0.4, 0.7)], 1.0, SCORCH, 0.12);
      for (let i = 0; i < 10; i++) {
        const a = Math.random() * TAU, sp = rnd(0.8, 2.4);
        toss('s:ember', () => bit(new THREE.OctahedronGeometry(0.09), EMBER), P.set(pos.x + Math.cos(a) * 0.5, 0.4, pos.z + Math.sin(a) * 0.5),
          V.set(Math.cos(a) * sp, rnd(3.5, 5.5), Math.sin(a) * sp), 0.8, 7, rnd(0.8, 1.3), 6);
      }
      for (let i = 0; i < 3; i++) puff(P.set(pos.x + rnd(-0.6, 0.6), 0.4, pos.z + rnd(-0.6, 0.6)), 0x4a4440, { size: 1.4, rise: 1.2, dur: 0.9 });
    },

    /* Стая coming down (the crows scatter from statuses()): a violet ink blot spreading over its reach, then gone. */
    flockLand({ pos }: { pos: Spot }) {
      const R = SKILLS.nightmare.Q.r, geo = anyBlot();
      spawn('s:violet' + geo.uuid, () => flat(fxMesh(geo, BRUISE), 0.23), 1.2, (it) => {
        it.obj.position.set(pos.x, 0.23, pos.z); it.obj.rotation.z = Math.random() * TAU;
      }, (it, k) => {
        const grow = 1 - Math.pow(1 - Math.min(1, k / 0.35), 3), fade = k > 0.5 ? 1 - (k - 0.5) / 0.5 : 1;
        it.obj.scale.setScalar(R * 0.6 * grow * fade + 0.001);
      });
      for (let i = 0; i < 6; i++) {
        const a = i / 6 * TAU + rnd(-0.3, 0.3), d = R * rnd(0.7, 0.95);
        blot(BRUISE, pos.x + Math.cos(a) * d, pos.z + Math.sin(a) * d, rnd(0.2, 0.35), 1.0, 0.235);
      }
    },
    /* Стая's fear on a victim: dark wisps leaving the head upward. */
    fear({ hero }: { hero?: ViewHero }) {
      if (!hero) return;
      for (let i = 0; i < 3; i++) {
        spawn('s:fright', () => fxMesh(new THREE.IcosahedronGeometry(0.24, 1), DUSK), 0.9, (it) => {
          it.h = hero; it.dx = (i - 1) * 0.4; it.ph = Math.random() * 6.3; it.dl = i * 0.1;
        }, (it, k) => {
          const u = after(k, it.dl), o = it.obj;
          if (u <= 0) { o.scale.setScalar(0.001); return; }
          const h = it.h, s = 1 - u;
          o.position.set(h.pos.x + it.dx + Math.sin(it.ph + u * 7) * 0.3 * u, 2.2 + u * 2 + h.lift, h.pos.z);
          o.scale.set(0.8 * s + 0.01, (1.6 + u * 2) * Math.min(1, u * 5) * s + 0.01, 0.8 * s + 0.01);
        });
      }
    },

    /* Тень на стене bursting, at each end: the dark pool snaps shut and shadow-blades jab up over its reach. */
    shadowBurst({ pos }: { pos: Spot }) {
      const R = SKILLS.shadow.W.r, geo = anyBlot();
      spawn('s:shut' + geo.uuid, () => flat(fxMesh(geo, DUSK), 0.24), 0.4, (it) => {
        it.obj.position.set(pos.x, 0.24, pos.z); it.r0 = Math.random() * TAU;
      }, (it, k) => { it.obj.scale.setScalar(R * 0.75 * (1 - Math.pow(k, 3)) + 0.001); it.obj.rotation.z = it.r0 + k * 1.2; });
      for (let i = 0; i < 9; i++) {
        const a = i / 9 * TAU + rnd(-0.25, 0.25), d = rnd(0.5, R * 0.9);
        spawn('s:blade', () => fxMesh(new THREE.ConeGeometry(0.08, 1.2, 4).translate(0, 0.6, 0), CROW), 0.45, (it) => {
          it.obj.position.set(pos.x + Math.cos(a) * d, 0.2, pos.z + Math.sin(a) * d);
          it.obj.rotation.set(rnd(-0.3, 0.3), 0, rnd(-0.3, 0.3));
          it.dl = rnd(0, 0.2); it.h = rnd(0.8, 1.3);
        }, (it, k) => {
          const u = after(k, it.dl);
          it.obj.scale.set(1, (u <= 0 ? 0 : env(u, 0.15, 0.45)) * it.h + 0.001, 1);
        });
      }
    },

    /* Соблазн: a crimson heart pops over the victim, petals falling from it. */
    charm({ hero }: { hero?: ViewHero }) {
      if (!hero) return;
      spawn('s:heart', () => fxMesh(heartGeometry(), CRIMSON), 0.7, (it) => { it.h = hero; }, (it, k) => {
        const h = it.h, pop = k < 0.2 ? k / 0.2 * 1.25 : k < 0.3 ? 1.25 - (k - 0.2) / 0.1 * 0.25 : 1, fade = k > 0.6 ? 1 - (k - 0.6) / 0.4 : 1;
        it.obj.position.set(h.pos.x, 2.7 + k * 0.6 + h.lift, h.pos.z);
        it.obj.rotation.set(-0.5, 0, 0);
        it.obj.scale.setScalar(2.4 * pop * fade + 0.001);
      });
      for (let i = 0; i < 4; i++) {
        const c = i % 2 ? CRIMSON : 0xd8a0a8;
        sink('s:petal' + c, () => bit(petalGeometry(), c, true), P.set(hero.pos.x + rnd(-0.3, 0.3), 2.8 + hero.lift, hero.pos.z + 0.3),
          V.set(rnd(-1.5, 1.5), rnd(0, 0.6), rnd(-0.5, 1.2)), 1.2, 1.6, 1.4);
      }
    },

    /* Расплата: crimson spokes drawn out from Грех to his ring's rim (the pool draws the rim), drops following them. */
    atonement({ hero }: { hero?: ViewHero }) {
      if (!hero) return;
      const R = SKILLS.sin.W.r;
      for (let i = 0; i < 10; i++) {
        const a = i / 10 * TAU;
        spawn('s:spoke', () => flat(fxMesh(strokeGeometry(1, 0.12, 0), CRIMSON, { double: true }), 0.27), 0.6, (it) => {
          it.h = hero; it.a = a;
        }, (it, k) => {
          const h = it.h, out = Math.min(1, k / 0.35);
          it.obj.position.set(h.pos.x + Math.cos(it.a) * 0.5, 0.27, h.pos.z + Math.sin(it.a) * 0.5);
          it.obj.rotation.set(-Math.PI / 2, 0, -it.a);
          it.obj.scale.set((R - 0.5) * out + 0.001, (k > 0.5 ? 1 - (k - 0.5) / 0.5 : 1) + 0.001, 1);
        });
      }
      for (let i = 0; i < 8; i++) {
        spawn('s:drop' + CRIMSON, () => fxMesh(new THREE.SphereGeometry(0.1, 8, 6), CRIMSON), 0.5, (it) => {
          it.h = hero; it.a = i / 8 * TAU;
        }, (it, k) => {
          const a = it.a - k * 2, d = 0.6 + R * k;
          it.obj.position.set(it.h.pos.x + Math.cos(a) * d, 1.2 - k * 0.9, it.h.pos.z + Math.sin(a) * d);
        });
      }
    },

    /* Наваждение: a bruise haze rolls out to the reach (the crows wheel on its rim), more rising off it. */
    delusion({ hero }: { hero?: ViewHero }) {
      if (!hero) return;
      const R = SKILLS.nightmare.W.r;
      for (let i = 0; i < 10; i++) {
        const a = i / 10 * TAU;
        puff(P.set(hero.pos.x + Math.cos(a) * 0.6, 0.35, hero.pos.z + Math.sin(a) * 0.6), 0x8a7ab8,
          { size: 1.2, rise: 0.2, dur: 0.8, drift: V.set(Math.cos(a) * R, 0, Math.sin(a) * R) });
      }
      for (let i = 0; i < 6; i++) {
        const a = i / 6 * TAU;
        puff(P.set(hero.pos.x + Math.cos(a) * 2, 0.4, hero.pos.z + Math.sin(a) * 2), 0x8a7ab8, { size: 1, rise: 1, dur: 0.8 });
      }
    },

    /* Перья: feathers burst out of the birdcatcher, a few left drifting down after. */
    feathers({ pos }: { pos: Spot }) {
      for (let i = 0; i < 14; i++) {
        const a = i / 14 * TAU;
        spawn('s:feather', () => fxMesh(new THREE.ConeGeometry(0.07, 0.5, 4), 0x6a6258), 0.5, (it) => {
          it.p = new THREE.Vector3(pos.x, 1.3, pos.z); it.v = new THREE.Vector3(Math.cos(a) * 6, 1.5, Math.sin(a) * 6);
        }, (it, k) => { it.obj.position.copy(it.p).addScaledVector(it.v, k * 0.5); it.obj.position.y += -2 * k * k; it.obj.rotation.set(k * 8, a, k * 5); });
      }
      for (let i = 0; i < 6; i++) {
        const a = Math.random() * TAU, c = i % 2 ? 0x6a6258 : 0x9a9084;
        sink('s:drift' + c, () => bit(featherGeometry(), c, true), P.set(pos.x + Math.cos(a) * 1.5, 2.2, pos.z + Math.sin(a) * 1.5),
          V.set(Math.cos(a) * 1.5, 0.5, Math.sin(a) * 1.5), 1.6, 1.1);
      }
    },

    /* Оберег (and the Мастер's shield): a hatched glass dome over the ally, thinning out stroke by stroke. */
    ward({ hero }: { hero: ViewHero }) {
      // Round tubes (no facet creases) left paper inside their contour: glass rims, not dark bars.
      const glass = (geo: THREE.BufferGeometry) => mk(geo, WARD_GLASS, { team: 'ghost', cast: false });
      spawn('s:dome', () => {
        const g = new THREE.Group();
        for (let i = 0; i < 4; i++) {  // a few meridians, a half circle each: sparse enough to read as glass, not bars
          const m = glass(new THREE.TorusGeometry(1, 0.03, 8, 24, Math.PI));
          m.rotation.y = i / 4 * Math.PI;
          g.add(m);
        }
        const lat = glass(new THREE.TorusGeometry(0.866, 0.03, 8, 40));  // one latitude, halfway up
        lat.rotation.x = Math.PI / 2; lat.position.y = 0.5;
        g.add(lat);
        return g;
      }, 0.75, (it) => { it.h = hero; }, (it, k) => {
        const h = it.h, o = it.obj, g = k < 0.15 ? 0.7 + 0.3 * k / 0.15 : 1, left = k < 0.45 ? 1 : 1 - (k - 0.45) / 0.55;
        o.position.set(h.pos.x, h.lift, h.pos.z);
        o.scale.set(1.25 * g, 1.8 * g, 1.25 * g);
        o.rotation.y = k * 0.8;
        o.children.forEach((c: THREE.Object3D, i: number) => { c.visible = (i + 0.5) / o.children.length < left; });
      });
    },
    /* The shield broken: the dome's glass flies apart. */
    wardBreak({ hero }: { hero: ViewHero }) {
      for (let i = 0; i < 12; i++) {
        const a = i / 12 * TAU + rnd(-0.2, 0.2), sp = rnd(2, 3.5);
        toss('s:glass', () => bit(new THREE.CircleGeometry(0.24, 3), GLASS, true), P.set(hero.pos.x + Math.cos(a), rnd(0.6, 2.0) + hero.lift, hero.pos.z + Math.sin(a)),
          V.set(Math.cos(a) * sp, rnd(1, 3), Math.sin(a) * sp), 0.6, 10, rnd(0.8, 1.3), 10);
      }
    },

    /* Облик Волчицы: ink and fur spiralling up round her, grey tufts flung off. */
    wolfForm({ hero }: { hero: ViewHero }) {
      for (let i = 0; i < 14; i++) {
        const c = i % 2 ? ink : FUR;
        spawn('s:swirl' + c, () => fxMesh(new THREE.IcosahedronGeometry(0.13, 0).scale(0.6, 0.6, 1.8), c), 0.7, (it) => {
          it.h = hero; it.a = i / 14 * TAU * 2; it.y0 = (i / 14) * 0.4;
        }, (it, k) => {
          const h = it.h, a = it.a + k * 9, r = 1.0 * (1 - k * 0.6);
          it.obj.position.set(h.pos.x + Math.cos(a) * r, 0.2 + it.y0 + k * 2 + h.lift, h.pos.z + Math.sin(a) * r);
          it.obj.rotation.set(0.4, -a, 0);  // drawn out along the spiral
          it.obj.scale.setScalar(1.3 * (1 - k) + 0.001);
        });
      }
      for (let i = 0; i < 5; i++) {
        const a = i / 5 * TAU + rnd(-0.3, 0.3);
        toss('s:tuft', () => bit(new THREE.IcosahedronGeometry(0.12, 0).scale(0.6, 0.6, 1.6), FUR), P.set(hero.pos.x, 1.2 + hero.lift, hero.pos.z),
          V.set(Math.cos(a) * 3, rnd(2, 3.5), Math.sin(a) * 3), 0.7, 9, rnd(1, 1.5), 6);
      }
      puff(P.set(hero.pos.x, 0.4, hero.pos.z), 0x5a5550, { size: 1.3, rise: 0.5, dur: 0.6 });
    },

    /* Щит Мученика: a gold halo flashing over the head, motes falling from it (the five seconds are `invulnerable`'s). */
    martyrShield({ hero }: { hero: ViewHero }) {
      spawn('s:halo', () => bit(new THREE.TorusGeometry(0.38, 0.05, 6, 28), GOLD), 0.8, (it) => { it.h = hero; }, (it, k) => {
        const h = it.h, pop = k < 0.15 ? k / 0.15 * 1.3 : k < 0.25 ? 1.3 - (k - 0.15) / 0.1 * 0.3 : 1, fade = k > 0.6 ? 1 - (k - 0.6) / 0.4 : 1;
        it.obj.position.set(h.pos.x, 2.75 + h.lift, h.pos.z);
        it.obj.rotation.set(Math.PI / 2, 0, 0);
        it.obj.scale.setScalar(1.4 * pop * fade + 0.001);
      });
      for (let i = 0; i < 5; i++) {
        spawn('s:mote', () => fxMesh(new THREE.OctahedronGeometry(0.09), MOTE), 0.8, (it) => { it.h = hero; it.a = i / 5 * TAU; }, (it, k) => {
          const h = it.h, a = it.a + k * 2;
          it.obj.position.set(h.pos.x + Math.cos(a) * 0.5, 2.7 - k * 1.6 + h.lift, h.pos.z + Math.sin(a) * 0.5);
          it.obj.scale.setScalar(1 - k + 0.01);
        });
      }
    },
    /* Око бестиария: an eye opening over the head, looking, closing. */
    bestiaryEye({ hero }: { hero: ViewHero }) {
      spawn('s:eye', () => {
        const g = new THREE.Group();
        const iris = fxMesh(new THREE.CircleGeometry(0.17, 16), AMBER), pupil = fxMesh(new THREE.CircleGeometry(0.08, 12), ink);
        iris.position.z = 0.01; pupil.position.z = 0.02;
        g.add(fxMesh(lensGeometry(), LENS, { double: true }), iris, pupil);
        return g;
      }, 1.1, (it) => { it.h = hero; }, (it, k) => {
        const h = it.h, open = env(k, 0.25, 0.75);
        it.obj.position.set(h.pos.x, 3.0 + h.lift, h.pos.z);
        it.obj.rotation.set(FACE, 0, 0);
        it.obj.scale.set(1.8, 1.8 * Math.max(0.04, open), 1.8);
        it.obj.children[1]!.position.x = it.obj.children[2]!.position.x = Math.sin(k * 9) * 0.1;  // looking about
      });
    },
  };
}
